import { inject, Injectable } from '@angular/core';
import { FEATURE_FLAG, FeatureFlagsService } from '@rms/core/feature-flags';
import { PermissionService } from '@rms/user';
import { combineLatest, map, Observable } from 'rxjs';

import { NavigationItem } from './navigation-item.interface';

@Injectable({
  providedIn: 'root',
})
export class NavigationLoaderService {
  public readonly items$: Observable<NavigationItem[]> = this.navigationFor(inject(PermissionService));

  private readonly featureFlags = inject(FeatureFlagsService);

  private navigationFor(permissions: PermissionService): Observable<NavigationItem[]> {
    return combineLatest([permissions.isSupervisor$, permissions.isPartner$]).pipe(
      map(([isSupervisor, isPartnerAdmin]) => this.buildNavigation(isSupervisor, isPartnerAdmin))
    );
  }

  private buildNavigation(isSupervisor: boolean, isPartnerAdmin: boolean): NavigationItem[] {
    const released = (flag: FEATURE_FLAG): boolean => this.featureFlags.enabled(flag);
    const items: [boolean, NavigationItem][] = [
      [
        released(FEATURE_FLAG.SHOWCASE),
        {
          type: 'link',
          label: 'layout.menu.showcase',
          route: '/showcase',
          icon: 'remix:Design/palette-line',
          hoverIcon: 'remix:Design/palette-fill',
          routerLinkActiveOptions: { exact: false },
        },
      ],
      [
        released(FEATURE_FLAG.HOME),
        {
          type: 'link',
          label: 'layout.menu.home',
          route: '/home',
          icon: 'remix:Buildings/home-smile-2-line',
          hoverIcon: 'remix:Buildings/home-smile-2-fill',
          routerLinkActiveOptions: { exact: true },
        },
      ],
      [
        isSupervisor,
        {
          type: 'link',
          label: 'layout.menu.partners',
          route: '/partners',
          icon: 'remix:Business/briefcase-line',
          hoverIcon: 'remix:Business/briefcase-fill',
          routerLinkActiveOptions: { exact: false },
        },
      ],
      [
        true,
        {
          type: 'link',
          label: 'layout.menu.customers',
          route: '/customers',
          icon: 'remix:User & Faces/user-heart-line',
          hoverIcon: 'remix:User & Faces/user-heart-fill',
          routerLinkActiveOptions: { exact: false },
        },
      ],
      [
        isPartnerAdmin && released(FEATURE_FLAG.TEAM),
        {
          type: 'link',
          label: 'layout.menu.team',
          route: '/team',
          icon: 'remix:User & Faces/team-line',
          hoverIcon: 'remix:User & Faces/team-fill',
          routerLinkActiveOptions: { exact: false },
        },
      ],
      [
        true,
        {
          type: 'external-link',
          label: 'layout.menu.tolt',
          url: 'https://app.tolt.com',
          icon: 'logo:tolt',
        },
      ],
    ];

    return items.filter(([visible]) => visible).map(([, item]) => item);
  }
}
